import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook, keepNearestOrderbookLevels, mergeOrderBook } from '../utils';

// WEEX has NO browser CORS (api-spot.weex.com returns no access-control-allow-origin),
// so every REST call MUST be routed through the proxy. We reuse `proxyAxios` (basic
// auth + proxy baseURL baked from env) and the same `/api/v1/proxy?url=<encoded target>`
// routing the shared SDK proxy client uses (src/shared/cex-sdk/lib/proxy-client.ts).
const WEEX_REST_BASE = 'https://api-spot.weex.com';
const WEEX_PROXY_PATH = '/api/v1/proxy';
const WEEX_WS_URL = 'wss://ws-spot.weex.com/v3/ws/public';

// WEEX v3 public WS depth only supports levels 15 and 200.
const WEEX_WS_DEPTH_LEVEL = 15;

// Build the full target URL with query params and route it through the proxy, mirroring
// exchangeProxyClient.get() in src/shared/cex-sdk/lib/proxy-client.ts.
const proxyGet = <T>(url: string, params: Record<string, string | number>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => search.append(key, String(value)));
  const query = search.toString();
  const targetUrl = query ? `${url}?${query}` : url;

  return proxyAxios.get<T>(WEEX_PROXY_PATH, { params: { url: targetUrl } });
};

// WEEX success code (mirrors shared/cex-sdk/exchanges/weex/rest.ts WEEX_SUCCESS_CODE).
const WEEX_SUCCESS_CODE = '00000';

// REST snapshot poll cadence (ms). Keeps panels populated even if the browser WS to
// WEEX is blocked/unsupported.
const WEEX_REST_POLL_INTERVAL = 5000;

// WEEX candle `period` values (mirror shared/cex-sdk/exchanges/weex/timeframes.ts and
// connector/weex/common/enum.go): 1m,5m,15m,30m,1h,4h,12h,1d,1w,1M.
// The shared `cexExchangesTimeframes` table carries bitget-style values for WEEX, so we
// map the page interval to the correct WEEX period locally instead.
const WEEX_PERIOD_BY_TIMEFRAME: Partial<Record<IChartTimeframe, string>> = {
  '1s': '1m',
  '10s': '1m',
  '30s': '1m',
  '1m': '1m',
  '3m': '5m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '2h': '1h',
  '4h': '4h',
  '6h': '4h',
  '8h': '4h',
  '12h': '12h',
  '24h': '1d',
  '1d': '1d',
  '2d': '1d',
  '3d': '1d',
  '5d': '1d',
  '168h': '1w',
  '1w': '1w',
  '1M': '1M',
};

// WEEX kline WS channel period suffix per connectWebSocket interval (ms).
const WEEX_PERIOD_BY_INTERVAL_MS: Record<number, string> = {
  60000: '1m',
  300000: '5m',
  900000: '15m',
  1800000: '30m',
  3600000: '1h',
  14400000: '4h',
  43200000: '12h',
  86400000: '1d',
  604800000: '1w',
};

// WEEX exchangeInfo returns a string `tickSize` (e.g. "0.01"), not an integer
// precision. Derive the decimal-place count so the orderbook aggregation tick logic
// (shared with bitget) works.
const precisionFromTickSize = (tickSize: string | undefined): number => {
  if (!tickSize) return 1;
  const num = Number(tickSize);
  if (!Number.isFinite(num) || num <= 0) return 1;
  const [, decimals = ''] = tickSize.split('.');
  const trimmed = decimals.replace(/0+$/, '');
  return trimmed.length || 0;
};

const toTuples = (levels: [string, string][] | { price: string; size: string }[]): [string, string][] =>
  levels.map(level => (Array.isArray(level) ? [level[0], level[1]] : [level.price, level.size]));

export class WeexStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;
  pollInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  weexSymbol: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;
  hasWsDepthSnapshot: boolean;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.weexSymbol = this.pair;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.hasWsDepthSnapshot = false;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const res = await proxyGet<{
        symbols?: { tickSize: string }[];
      }>(`${WEEX_REST_BASE}/api/v3/exchangeInfo`, { symbol: this.weexSymbol });

      const prec = precisionFromTickSize(res.data?.symbols?.[0]?.tickSize);
      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Weex ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const period = WEEX_PERIOD_BY_TIMEFRAME[interval] ?? '1m';
    const symbol = `${base}${quote}`.toUpperCase();

    try {
      // v3 klines: [openTime, o, h, l, c, baseVol, closeTime, quoteVol, ...]
      const result = await proxyGet<(string | number)[][]>(
        `${WEEX_REST_BASE}/api/v3/market/klines`,
        { symbol, interval: period },
      );

      const rows = Array.isArray(result.data) ? result.data : [];
      return rows.map(el => [
        Number(el[0]),
        String(el[1]),
        String(el[2]),
        String(el[3]),
        String(el[4]),
        String(el[5]),
      ]) as KLine[];
    } catch (error) {
      console.log('Weex klines error: ', error);

      return [];
    }
  };

  // WEEX exposes NO public REST trades/fills endpoint (connector/weex/market/rest only
  // has ticker/exchangeInfo/depth/candles). Live trades therefore arrive via the WS
  // `trades.SYM` channel and feed onTradeUpdate; the initial REST snapshot is empty.
  fetchTrades = async (_args: { base: string; quote: string; limit: number }) => {
    return [] as TradeSocket[];
  };

  fetchDepthSnapshot = async () => {
    try {
      if (this.hasWsDepthSnapshot) return;

      const res = await proxyGet<{
        asks?: [string, string][];
        bids?: [string, string][];
      }>(`${WEEX_REST_BASE}/api/v3/market/depth`, {
        symbol: this.weexSymbol,
        limit: WEEX_WS_DEPTH_LEVEL,
      });

      if (this.hasWsDepthSnapshot) return;
      if (!res.data?.asks || !res.data?.bids) return;

      const responseAsks = res.data.asks ?? [];
      const responseBids = res.data.bids ?? [];

      const nearest = keepNearestOrderbookLevels(
        mergeOrderBook(responseAsks, []) as [string, string][],
        mergeOrderBook(responseBids, []) as [string, string][],
        WEEX_WS_DEPTH_LEVEL,
      );
      this.asks = nearest.asks;
      this.bids = nearest.bids;

      this.publishOrderbook();
    } catch (error) {
      console.log('Weex depth snapshot error: ', error);
    }
  };

  fetchTickerSnapshot = async () => {
    try {
      const res = await proxyGet<{
        lastPrice?: string;
        highPrice?: string;
        lowPrice?: string;
        volume?: string;
        quoteVolume?: string;
        priceChangePercent?: string;
      }>(`${WEEX_REST_BASE}/api/v3/market/ticker/24hr`, { symbol: this.weexSymbol });

      const t = res.data;
      if (!t?.lastPrice) return;

      this.onTickerUpdate({
        baseVolume: t.volume,
        change: normalizeNumber(Number(t.priceChangePercent) * 100),
        close: t.lastPrice,
        high: t.highPrice,
        last: Number(t.lastPrice).toFixed(this.precision),
        low: t.lowPrice,
        quoteVolume: t.quoteVolume,
      });
    } catch (error) {
      console.log('Weex ticker snapshot error: ', error);
    }
  };

  publishOrderbook = () => {
    this.onOrderbookUpdate(
      aggregateOrderbook(
        this.asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.selectedPrecision,
        this.depth,
      ),
    );
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket(WEEX_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    const periodSuffix = WEEX_PERIOD_BY_INTERVAL_MS[interval] ?? '1m';
    const tickerStream = `${this.weexSymbol}@ticker`;
    const depthStream = `${this.weexSymbol}@depth${WEEX_WS_DEPTH_LEVEL}`;
    const tradesStream = `${this.weexSymbol}@trade`;
    const klineStream = `${this.weexSymbol}@kline_${periodSuffix}_LAST_PRICE`;

    // REST snapshots so the panels populate immediately and stay fresh even if the
    // browser WS to WEEX is blocked/unsupported.
    this.fetchDepthSnapshot();
    this.fetchTickerSnapshot();

    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    this.pollInterval = setInterval(() => {
      this.fetchDepthSnapshot();
      this.fetchTickerSnapshot();
    }, WEEX_REST_POLL_INTERVAL);

    this.ws.onopen = () => {
      [tickerStream, depthStream, tradesStream, klineStream].forEach((stream, id) => {
        ws.send(JSON.stringify({ method: 'SUBSCRIBE', params: [stream], id: id + 1 }));
      });
    };

    this.ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data?.event === 'ping') {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ event: 'pong', time: data.time }));
          }
          return;
        }
        if (data?.type === 'ping') {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ method: 'PONG', id: data.id }));
          }
          return;
        }
        if (data?.event === 'connected' || data?.event === 'pong' || data?.result !== undefined) {
          return;
        }

        if (data?.e === 'ticker' && data?.d?.[0]) {
          const row = data.d[0];
          this.onTickerUpdate({
            baseVolume: row.v,
            change: normalizeNumber(Number(row.P) * 100),
            close: row.c,
            high: row.h,
            last: Number(row.c).toFixed(this.precision),
            low: row.l,
            quoteVolume: row.q,
          });
        }

        if (data?.e === 'kline' && data?.d?.[0]) {
          const last = data.d[data.d.length - 1];
          this.klineUpdate([
            Math.floor(Number(last.t) / 1000),
            String(last.o),
            String(last.h),
            String(last.l),
            String(last.c),
            String(last.v),
          ]);
        }

        if (data?.e === 'depth' || data?.e === 'depthSnapshot') {
          const depthType = data.d === 'SNAPSHOT' || data.e === 'depthSnapshot' ? 'SNAPSHOT' : 'CHANGED';
          const responseAsks = toTuples(data.a ?? []);
          const responseBids = toTuples(data.b ?? []);
          const asks =
            depthType === 'SNAPSHOT'
              ? (mergeOrderBook(responseAsks, []) as [string, string][])
              : (mergeOrderBook(this.asks, responseAsks) as [string, string][]);
          const bids =
            depthType === 'SNAPSHOT'
              ? (mergeOrderBook(responseBids, []) as [string, string][])
              : (mergeOrderBook(this.bids, responseBids) as [string, string][]);
          this.hasWsDepthSnapshot = true;
          const nearest = keepNearestOrderbookLevels(asks, bids, WEEX_WS_DEPTH_LEVEL);
          this.asks = nearest.asks;
          this.bids = nearest.bids;
          this.publishOrderbook();
        }

        if ((data?.e === 'trade' || data?.e === 'tradeSnapshot') && data?.d?.[0]) {
          const row = data.d[0];
          this.onTradeUpdate({
            price: row.p,
            quantity: row.q,
            timestamp: String(row.T),
            side: row.m ? 'sell' : 'buy',
          });
        }
      } catch (error) {
        console.log('Weex socket error', error);
      }
    };

  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
      this.ws = undefined;
    }
    if (this.depthWs) {
      this.depthWs.close();
      this.depthWs = undefined;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
    }
    this.hasWsDepthSnapshot = false;
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    // WEEX depth WS is a fixed 200-level book with no server-side scaling, so the tick
    // switch is a pure client-side re-aggregation.
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth(depth: number) {
    this.depth = depth;
    this.publishOrderbook();
  }

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new WeexStrategy(args);

    await instance.fetch();

    return instance;
  };
}
